import type { UseFormSetValue } from 'react-hook-form';

import type {
    AddressFormValues,
    FormatAddressResponseInterface,
    FormattedAddressFieldInterface,
    FormattedEdqAddress,
    SuggestedAddressInterface,
} from './types';

export function getFieldErrorMessage(error: unknown): string | undefined {
    if (!error) return undefined;
    if (typeof error === 'string') return error;
    if (Array.isArray(error) && typeof error[0] === 'string') return error[0];
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const message = (error as { message?: unknown }).message;
        return typeof message === 'string' ? message : undefined;
    }
    return undefined;
}

export function parseFormattedEdqAddress(addressFields: FormattedAddressFieldInterface[]): FormattedEdqAddress {
    return {
        streetAddress: addressFields[0]?.addressLine1,
        apt: addressFields[1]?.addressLine2,
        city: addressFields[3]?.locality,
        state: addressFields[4]?.province,
        zipCode:
            addressFields[6]?.country?.toUpperCase() === 'UNITED STATES OF AMERICA'
                ? addressFields[5]?.postalCode.split('-')[0]
                : addressFields[5]?.postalCode,
    };
}

export function getFormattedAddressFromResponse(
    formatAddressResponse: FormatAddressResponseInterface | null | undefined
): FormattedEdqAddress | null {
    if (!formatAddressResponse?.address) {
        return null;
    }

    return parseFormattedEdqAddress(formatAddressResponse.address);
}

export function applyFormattedAddressToFormik(
    formattedAddress: FormattedEdqAddress,
    values: AddressFormValues,
    setValues: (values: AddressFormValues) => void
): void {
    setValues({
        ...values,
        address: {
            ...values.address,
            ...formattedAddress,
        },
    });
}

export function applyFormattedAddressToRHF(
    formattedAddress: FormattedEdqAddress,
    setValue: UseFormSetValue<AddressFormValues>
): void {
    setValue('address.streetAddress', formattedAddress.streetAddress ?? '', { shouldDirty: true });
    setValue('address.apt', formattedAddress.apt ?? '', { shouldDirty: true });
    setValue('address.city', formattedAddress.city ?? '', { shouldDirty: true });
    setValue('address.state', formattedAddress.state ?? '', { shouldDirty: true });
    setValue('address.zipCode', formattedAddress.zipCode ?? '', { shouldDirty: true });
}

export function createSuggestedAddressSelectionHandler(
    handleSuggestedAddressClick: (
        suggestedAddress: SuggestedAddressInterface
    ) => Promise<FormatAddressResponseInterface | null>,
    onFormattedAddress: (formattedAddress: FormattedEdqAddress) => void
): (suggestedAddress: SuggestedAddressInterface) => Promise<void> {
    return async suggestedAddress => {
        const formatAddressResponse = await handleSuggestedAddressClick(suggestedAddress);
        const formattedAddress = getFormattedAddressFromResponse(formatAddressResponse);

        if (formattedAddress) {
            onFormattedAddress(formattedAddress);
        }
    };
}
